import { TOUR } from './targets';
import type {
  PostSubmitOutcome,
  RunKind,
  SectionId,
  SectionStatus,
  SubmitSectionId,
  TourPage,
  TourSection,
  TourStep,
} from './types';

const exact = (path: string) => (pathname: string) => pathname === path;
const startsWith = (prefix: string) => (pathname: string) => pathname.startsWith(prefix);
const editorOf = (base: string) => {
  const edit = new RegExp(`^${base}/[^/]+/edit$`);
  return (pathname: string) => pathname === `${base}/new` || edit.test(pathname);
};

/**
 * Wording rules (product owner): describe how to use each control. Do not
 * quote review thresholds or other admin policy; say "reviewed, published on
 * approval, publishes directly after several approvals". Do not point out
 * which data is withheld from hosts. English only; no dashes, no emoji.
 */

const activitiesPages: TourPage[] = [
  {
    key: 'list',
    matches: exact('/host/activities'),
    entry: '/host/activities',
    readyTarget: TOUR.activitiesTabs,
  },
];

const activitiesSteps: TourStep[] = [
  {
    id: 'welcome',
    page: 'list',
    title: 'Welcome to your host dashboard',
    body:
      'This guide walks you through each page of the dashboard. ' +
      'You can do real work while it runs. ' +
      'Use <b>Skip section</b> to jump past a page you do not need, and replay any section later from the <b>Guide</b> button in the tab bar.',
    nextLabel: 'Start',
  },
  {
    id: 'nav',
    page: 'list',
    target: TOUR.navTabs,
    title: 'Tab bar',
    body:
      'Everything you manage as a host lives under these tabs. ' +
      '<b>Guide</b> reopens this walkthrough any time and <b>View Site</b> opens the public website.',
    side: 'bottom',
    align: 'start',
  },
  {
    id: 'tabs',
    page: 'list',
    target: TOUR.activitiesTabs,
    title: 'Active, Past, Draft',
    body:
      '<b>Active</b> lists published activities with upcoming sessions. <b>Past</b> holds published activities whose sessions have all ended. ' +
      '<b>Draft</b> holds activities that are not published yet, including new ones waiting for review.',
    side: 'bottom',
    align: 'start',
  },
  {
    id: 'sessions',
    page: 'list',
    target: TOUR.activityCardSessions,
    title: 'Sessions on the card',
    body:
      'Upcoming and past sessions are listed under each card. A row shows the time, how many spots are booked, how many families are on the waitlist, ' +
      'and a <b>View</b> link that opens the sign-up roster for that session, which you can export as CSV. Sessions created with Repeat carry a Series tag.',
    side: 'right',
  },
  {
    id: 'create',
    page: 'list',
    target: TOUR.activitiesCreate,
    title: 'Create your first activity',
    body: 'Click <b>Create Activity</b> to open the form. The next section walks through every field, and you can fill it in for real as you go.',
    side: 'bottom',
    align: 'end',
    advanceOnClick: true,
    nextLabel: 'Open the form',
  },
];

const activityFormPages: TourPage[] = [
  {
    key: 'form',
    matches: editorOf('/host/activities'),
    entry: '/host/activities/new',
    readyTarget: TOUR.afTitle,
  },
];

const activityFormSteps: TourStep[] = [
  {
    id: 'intro',
    page: 'form',
    title: 'The activity form',
    body:
      'This is where an in-person activity is defined. Everything you enter here is real: when you click <b>Create Activity</b> at the bottom, ' +
      'the activity is submitted for review. You can also just read along and click Next.',
    nextLabel: 'Start',
  },
  {
    id: 'media',
    page: 'form',
    target: TOUR.afMedia,
    title: 'Cover image',
    body: 'Upload photos or short videos (JPG, PNG, WEBP, MP4, MOV). Drag to reorder. Hover a picture and click <b>Set Cover</b> to choose the image shown on cards and at the top of the page.',
    side: 'bottom',
  },
  {
    id: 'title',
    page: 'form',
    target: TOUR.afTitle,
    title: 'Title',
    body:
      'Write the title in Chinese, English, or both. If you leave one language empty, we fill it with a translation that you can edit. ' +
      'Families see the title in the language they browse the site in.',
    side: 'bottom',
  },
  {
    id: 'description',
    page: 'form',
    target: TOUR.afDescription,
    title: 'Description',
    body:
      'Describe what families can expect: the schedule, what to bring, who it suits. Like the title, write in either language or both, and we translate the empty one. ' +
      'The toolbar adds bold, lists, links and photos inside the text, and <b>Preview</b> shows exactly what families will see. The cover and other page photos go in Cover Image at the top.',
    side: 'top',
  },
  {
    id: 'type',
    page: 'form',
    target: TOUR.afType,
    title: 'Who takes part',
    body:
      '<b>Parent and child</b> means a child attends together with an adult. <b>Kids only</b> means children take part on their own and adults do not need to sign up. <b>Adults only</b> has no children and locks the age range to 18 and over. ' +
      'Choose this first, then the teaching language and the prices.',
    side: 'top',
  },
  {
    id: 'adult-price',
    page: 'form',
    target: TOUR.afAdultPrice,
    title: 'Adult price',
    body: 'Optional. Leave it empty or enter 0 if accompanying adults join for free.',
    side: 'left',
  },
  {
    id: 'capacity',
    page: 'form',
    target: TOUR.afCapacity,
    title: 'Capacity counting',
    body:
      'Controls who takes up a spot in a session. <b>Adults count toward capacity</b> is off by default, so only children use spots. ' +
      'When the child price is 0 you also see <b>Free children count toward capacity</b>. ' +
      'Example: capacity 10 with only children counting allows 10 children with any number of adults.',
    side: 'top',
  },
  {
    id: 'regions',
    page: 'form',
    target: TOUR.afRegions,
    title: 'Regions',
    body: 'Required. Choose every Bay Area region the activity serves: South Bay, Peninsula, East Bay, San Francisco or Other. Families filter by region.',
    side: 'right',
  },
  {
    id: 'location',
    page: 'form',
    target: TOUR.afLocation,
    title: 'Location',
    body: 'The venue name or address. It is shown on the public activity page, so write it the way you want families to see it.',
    side: 'left',
  },
  {
    id: 'deadline',
    page: 'form',
    target: TOUR.afDeadline,
    title: 'Registration deadline',
    body: 'When booking closes for each session. The default is the session end time. You can also close it at the start time, or a number of hours or days before the start.',
    side: 'top',
  },
  {
    id: 'add-session',
    page: 'form',
    target: TOUR.afAddSession,
    title: 'Add a session',
    body: 'A session is one dated occurrence of the activity. Click <b>Add Session</b> to add the first one.',
    side: 'left',
    advanceOnClick: true,
  },
  {
    id: 'session',
    page: 'form',
    target: TOUR.afSessionItem,
    title: 'Session details',
    body:
      'Set the start and end time and the capacity. <b>Child Price</b> and <b>Adult Price</b> here are optional overrides; leave them empty to use the activity prices. ' +
      '<b>Description</b> is an optional short label such as Holiday special or Beginner level, shown to families next to the session. Write it in either language.',
    side: 'top',
    waitMs: 1500,
  },
  {
    id: 'repeat',
    page: 'form',
    target: TOUR.afSessionRepeat,
    title: 'Repeat a session',
    body:
      'For a weekly class or a daily camp, set <b>Repeat</b> to Every Day, Every Week, Every Month or Custom, then choose how many times or an end date. ' +
      'A preview of the dates appears below. The sessions are created as one series and grouped together when you edit later.',
    side: 'top',
  },
  {
    id: 'submit',
    page: 'form',
    target: TOUR.afSubmit,
    title: 'Submit for review',
    body:
      'Check everything, then click <b>Create Activity</b>. New activities are reviewed before they go live and you get an email when the activity is published. ' +
      'After several approved activities, new ones publish directly. Published activities can be edited any time. ' +
      'A session that already has bookings can only be cancelled by the YOYO team, so reach out if you need that.',
    side: 'top',
    align: 'end',
  },
];

const coursesPages: TourPage[] = [
  {
    key: 'list',
    matches: exact('/host/courses'),
    entry: '/host/courses',
    readyTarget: TOUR.coursesTabs,
  },
  {
    key: 'editor',
    matches: editorOf('/host/courses'),
    entry: '/host/courses/new',
    readyTarget: TOUR.ceTitle,
  },
];

const coursesSteps: TourStep[] = [
  {
    id: 'intro',
    page: 'list',
    title: 'Online courses (optional)',
    body:
      'Courses are recorded video lessons that families buy and watch online. If you only run in-person activities, skip this section. ' +
      'You can always come back to it from the Guide button.',
    nextLabel: 'Show me courses',
  },
  {
    id: 'tabs',
    page: 'list',
    target: TOUR.coursesTabs,
    title: 'Course status tabs',
    body:
      '<b>Active</b> shows courses that are live. <b>In review</b> holds courses waiting for lesson approval. <b>Draft</b> holds courses you have not activated yet. ' +
      '<b>Inactive</b> holds courses you took offline.',
    side: 'bottom',
    align: 'start',
  },
  {
    id: 'card',
    page: 'list',
    target: TOUR.courseCard,
    title: 'Course card',
    body:
      'Each card shows the cover, titles, price, lesson count and copies sold. <b>Sales by lesson</b> expands to show how many buyers each lesson has, ' +
      'and its <b>View</b> link opens the full sales report with an Export CSV button. Edit, View and Delete work like activities.',
    side: 'right',
  },
  {
    id: 'create',
    page: 'list',
    target: TOUR.coursesCreate,
    title: 'Create a course',
    body: 'Click <b>Create Course</b> to open the editor.',
    side: 'bottom',
    align: 'end',
    advanceOnClick: true,
    nextLabel: 'Open the editor',
  },
  {
    id: 'price',
    page: 'editor',
    target: TOUR.cePrice,
    title: 'Default lesson price',
    body: 'Each lesson is sold at this USD price unless you override it per lesson further down. CNY is not charged at the moment, so you can leave it empty.',
    side: 'top',
  },
  {
    id: 'images',
    page: 'editor',
    target: TOUR.ceImages,
    title: 'Cover images',
    body: 'Cover and intro pictures for the course page (JPG, PNG, WEBP). <b>Set cover</b> picks the main image. Videos are not uploaded here; they go in Lessons.',
    side: 'top',
  },
  {
    id: 'save',
    page: 'editor',
    target: TOUR.ceSave,
    title: 'Save the course first',
    body: 'Click <b>Create course</b> to save. Lessons can only be added to a saved course, so the editor reopens in edit mode after saving.',
    side: 'right',
  },
  {
    id: 'lessons',
    page: 'editor',
    target: TOUR.ceLessons,
    title: 'Lessons',
    body:
      'Each lesson is one video. Give it a title in Chinese, English, or both, such as Lesson 1, and click <b>Add lesson</b>. Then use <b>Upload video file</b> (MP4, MOV or WEBM). ' +
      'You can <b>Rename</b> a lesson later. ' +
      'Keep the page open until the upload finishes. Each lesson can have its own <b>Price USD</b>; <b>Save price</b> stores it, and blank means the course default.',
    side: 'top',
  },
  {
    id: 'status',
    page: 'editor',
    target: TOUR.ceLessonStatus,
    title: 'Review and going live',
    body:
      'Uploaded videos are reviewed before families can watch them. When all lessons are ready, click <b>Set active</b> to publish. ' +
      'If a lesson is still in review, the course goes live automatically once it is approved. <b>Withdraw</b> pulls a course out of review and <b>Take offline</b> hides a live course. ' +
      'If a lesson is not approved you get an email with the reason and can upload a new file.',
    side: 'left',
  },
  {
    id: 'preview',
    page: 'editor',
    target: TOUR.cePreview,
    title: 'Preview and view',
    body: '<b>Preview</b> (or <b>View</b> once the course is live) opens the course page exactly as families see it, so you can check the layout before publishing.',
    side: 'bottom',
    align: 'end',
  },
];

const passesPages: TourPage[] = [
  {
    key: 'list',
    matches: exact('/host/passes'),
    entry: '/host/passes',
    readyTarget: TOUR.passesTabs,
  },
  {
    key: 'form',
    matches: editorOf('/host/passes'),
    entry: '/host/passes/new',
    readyTarget: TOUR.pfActivity,
  },
];

const passesSteps: TourStep[] = [
  {
    id: 'intro',
    page: 'list',
    title: 'Passes',
    body: 'A pass is a prepaid bundle: a family buys a number of sessions of one activity up front and books them one at a time. It works well for weekly classes.',
    nextLabel: 'Start',
  },
  {
    id: 'tabs',
    page: 'list',
    target: TOUR.passesTabs,
    title: 'Pass status tabs',
    body:
      '<b>Active</b> passes are on sale. <b>Pending Review</b> passes are waiting for approval. <b>Paused</b> passes are hidden from sale but still usable by families who bought them. ' +
      '<b>Expired</b> and <b>Sold Out</b> are past their date or limit. <b>All</b> shows everything.',
    side: 'bottom',
    align: 'start',
  },
  {
    id: 'actions',
    page: 'list',
    target: TOUR.passCardActions,
    title: 'Edit, View, Pause',
    body:
      '<b>Edit</b> opens the form. <b>View</b> shows the pass details. <b>Pause</b> takes an active pass off sale and <b>Activate</b> puts it back. ' +
      '<b>Delete</b> is available only while nothing has been sold.',
    side: 'top',
  },
  {
    id: 'create',
    page: 'list',
    target: TOUR.passesCreate,
    title: 'Create a pass',
    body: 'Click <b>Create Pass</b> to open the form.',
    side: 'bottom',
    align: 'end',
    advanceOnClick: true,
    nextLabel: 'Open the form',
  },
  {
    id: 'activity',
    page: 'form',
    target: TOUR.pfActivity,
    title: 'Choose the activity',
    body: 'A pass belongs to one activity and cannot be moved to another after it is created, so pick carefully.',
    side: 'bottom',
    align: 'start',
  },
  {
    id: 'total',
    page: 'form',
    target: TOUR.pfTotalSessions,
    title: 'Total sessions',
    body: 'How many session bookings the pass includes. It locks once the first pass is sold.',
    side: 'top',
  },
  {
    id: 'expires',
    page: 'form',
    target: TOUR.pfExpires,
    title: 'Expiration date',
    body: 'The last day the pass can be used. Once buyers exist it can be extended but not shortened.',
    side: 'top',
  },
  {
    id: 'sessions',
    page: 'form',
    target: TOUR.pfSessions,
    title: 'Available sessions',
    body:
      'Tick the sessions that pass holders may book, or use <b>Select All</b>. When you add new sessions to the activity later, come back and tick them here too, ' +
      'otherwise pass holders cannot book them.',
    side: 'top',
  },
  {
    id: 'submit',
    page: 'form',
    target: TOUR.pfSubmit,
    title: 'Submit',
    body: 'Click <b>Create Pass</b>. New passes are reviewed before they go on sale and you get an email when approved. After several approved passes, new ones go on sale directly.',
    side: 'top',
    align: 'start',
  },
];

const bookingsPages: TourPage[] = [
  {
    key: 'list',
    matches: exact('/host/bookings'),
    entry: '/host/bookings',
    readyTarget: TOUR.bookingsTabs,
  },
];

const bookingsSteps: TourStep[] = [
  {
    id: 'tabs',
    page: 'list',
    target: TOUR.bookingsTabs,
    title: 'Bookings',
    body:
      'Every booking across all your activities, in one table. Filter it with <b>All</b>, <b>Confirmed</b>, <b>Cancelled</b> and <b>Transferred</b>. ' +
      'Transferred means the family moved the booking to another session.',
    side: 'bottom',
    align: 'start',
  },
  {
    id: 'consent',
    page: 'list',
    target: TOUR.bookingsColConsent,
    title: 'Photo consent',
    body: 'Each family’s photo permission: <b>Full</b>, <b>Blurred</b> or <b>No Photo</b>. Check it before taking pictures at the session.',
    side: 'bottom',
  },
  {
    id: 'view',
    page: 'list',
    target: TOUR.bookingsRowActions,
    title: 'View details',
    body: '<b>View</b> opens the full booking: participant names and ages, notes from the family, phone number and payment details.',
    side: 'left',
  },
  {
    id: 'export',
    page: 'list',
    target: TOUR.bookingsExport,
    title: 'Export CSV',
    body: 'Click <b>Export CSV</b> to download bookings as a spreadsheet.',
    side: 'bottom',
    align: 'end',
    advanceOnClick: true,
    nextLabel: 'Open export',
  },
  {
    id: 'dataset',
    page: 'list',
    target: TOUR.bxDataset,
    title: 'Bookings or Course Sales',
    body: 'In the Export Data window, choose <b>Bookings</b> for in-person activities or <b>Course Sales</b> for online course purchases.',
    side: 'bottom',
    waitMs: 1500,
  },
  {
    id: 'filters',
    page: 'list',
    target: TOUR.bxFilters,
    title: 'Narrow it down',
    body: 'Filter by session date range, and by a single activity or session. Leave everything empty to export all bookings. Then click <b>Export</b>.',
    side: 'top',
    waitMs: 500,
  },
];

const emailPages: TourPage[] = [
  {
    key: 'list',
    matches: exact('/host/email'),
    entry: '/host/email',
    readyTarget: TOUR.emailFilter,
  },
];

const emailSteps: TourStep[] = [
  {
    id: 'intro',
    page: 'list',
    title: 'Emails to your participants',
    body:
      'For each upcoming session you can send two emails to everyone who booked: an <b>Event Detail</b> email before the session and a <b>Reminder</b> closer to the date. ' +
      'Both are sent by YOYO on your behalf.',
    nextLabel: 'Start',
  },
  {
    id: 'filter',
    page: 'list',
    target: TOUR.emailFilter,
    title: 'Filter by activity',
    body: 'Pick one activity to see only its sessions. Choosing an activity also unlocks bulk scheduling.',
    side: 'bottom',
    align: 'start',
  },
  {
    id: 'bulk',
    page: 'list',
    target: TOUR.emailBulk,
    title: 'Bulk schedule',
    body:
      'With an activity selected, a bulk toolbar appears. Tick sessions (or <b>Select all</b>) and click <b>Schedule detail email</b> to set the same link, message and timing for up to 100 sessions at once. ' +
      'Sessions that cannot be selected show the reason when you hover the checkbox.',
    side: 'bottom',
    align: 'start',
  },
  {
    id: 'link',
    page: 'list',
    target: TOUR.emailDetailLink,
    title: 'Detail link',
    body: 'An optional link included in the Event Detail email, for example a map or a page with directions. Click <b>Add Link</b>, paste the URL and <b>Save</b>.',
    side: 'bottom',
    align: 'start',
  },
  {
    id: 'detail',
    page: 'list',
    target: TOUR.emailDetailCol,
    title: 'Event Detail email',
    body:
      'The status line shows Not scheduled, Scheduled, Sending, Sent or Partial. <b>Add Message</b> lets you write up to 3000 characters for the families: meeting point, what to bring, parking. ' +
      'Save the message before scheduling.',
    side: 'top',
    align: 'start',
  },
  {
    id: 'actions',
    page: 'list',
    target: TOUR.emailDetailActions,
    title: 'Schedule, Preview, Recipients',
    body:
      '<b>Schedule</b> offers 48 hours before the session, a time you pick, or Send Now. <b>Preview</b> shows the exact email before you commit. ' +
      '<b>Recipients</b> lists who received it, and if a send is partial you can <b>Retry</b> the failed ones.',
    side: 'top',
    align: 'start',
  },
  {
    id: 'reminder',
    page: 'list',
    target: TOUR.emailReminderCol,
    title: 'Reminder email',
    body: 'Works the same way with a shorter message of up to 500 characters. It is typically scheduled a day or two before the session.',
    side: 'top',
    align: 'start',
  },
  {
    id: 'review',
    page: 'list',
    title: 'Review, and where families see it',
    body:
      'Emails you schedule may be reviewed before they go out. Once approved they send at the time you chose and you are notified; after several approvals your emails send without review. ' +
      'Once submitted, the link and message are locked for that session. ' +
      'Families can also read your Event Detail and Reminder content on the website under their booking, not only in their inbox.',
  },
];

const finishPages: TourPage[] = [
  {
    key: 'any',
    matches: startsWith('/host/'),
    entry: '/host/activities',
  },
];

const finishSteps: TourStep[] = [
  {
    id: 'done',
    page: 'any',
    target: TOUR.guideButton,
    title: 'You are all set',
    body:
      'That is every page of the host dashboard. A few things the YOYO team handles for you, so just reach out: cancelling a session that already has bookings, ' +
      'changing your display name, avatar or bio, waitlists and refunds. ' +
      'Replay any section any time from this <b>Guide</b> button.',
    side: 'bottom',
    align: 'end',
    nextLabel: 'Finish',
  },
];

export const SECTIONS: TourSection[] = [
  {
    id: 'activities',
    title: 'Activities',
    autoStart: true,
    pages: activitiesPages,
    steps: activitiesSteps,
  },
  {
    id: 'activity-form',
    title: 'Create Activity',
    autoStart: true,
    pages: activityFormPages,
    steps: activityFormSteps,
  },
  {
    id: 'courses',
    title: 'Courses',
    optional: true,
    autoStart: true,
    pages: coursesPages,
    steps: coursesSteps,
  },
  { id: 'passes', title: 'Passes', autoStart: true, pages: passesPages, steps: passesSteps },
  {
    id: 'bookings',
    title: 'Bookings',
    autoStart: true,
    pages: bookingsPages,
    steps: bookingsSteps,
  },
  { id: 'email', title: 'Email', autoStart: true, pages: emailPages, steps: emailSteps },
  { id: 'finish', title: 'Finish', autoStart: false, pages: finishPages, steps: finishSteps },
];

/**
 * Follow-up cards shown on a list page right after the host really submitted
 * the form during a run, keyed by where the item landed. Sections without a
 * `past` variant fall back to `live`.
 */
export interface PostSubmitSpec {
  route: string;
  readyTarget: string;
  steps: { pending: TourStep; live: TourStep; past?: TourStep };
}

export function postSubmitStep(spec: PostSubmitSpec, outcome: PostSubmitOutcome): TourStep {
  return spec.steps[outcome] ?? spec.steps.live;
}

export const POST_SUBMIT_STEPS: Record<SubmitSectionId, PostSubmitSpec> = {
  'activity-form': {
    route: '/host/activities',
    readyTarget: TOUR.activitiesTabs,
    steps: {
      pending: {
        id: 'submitted',
        page: 'list',
        target: TOUR.activitiesTabDraft,
        title: 'Submitted for review',
        body: 'It sits under <b>Draft</b> while it is pending review. You get an email when it is published.',
        side: 'bottom',
        align: 'start',
        nextLabel: 'Continue',
      },
      live: {
        id: 'live',
        page: 'list',
        target: TOUR.activitiesTabActive,
        title: 'Your activity is live',
        body: 'It sits under <b>Active</b>. <b>View</b> opens the public page.',
        side: 'bottom',
        align: 'start',
        nextLabel: 'Continue',
      },
      past: {
        id: 'past',
        page: 'list',
        target: TOUR.activitiesTabPast,
        title: 'Published, but nothing upcoming',
        body: 'Every session you added has already ended, so it sits under <b>Past</b>. Add an upcoming session to make it bookable.',
        side: 'bottom',
        align: 'start',
        nextLabel: 'Continue',
      },
    },
  },
  passes: {
    route: '/host/passes',
    readyTarget: TOUR.passesTabs,
    steps: {
      pending: {
        id: 'submitted',
        page: 'list',
        target: TOUR.passesTabPending,
        title: 'Pass submitted for review',
        body: 'It sits under <b>Pending Review</b>. You get an email when it goes on sale.',
        side: 'bottom',
        align: 'start',
        nextLabel: 'Continue',
      },
      live: {
        id: 'live',
        page: 'list',
        target: TOUR.passesTabActive,
        title: 'Your pass is on sale',
        body: 'It sits under <b>Active</b> and is on sale. <b>Pause</b> takes it off sale.',
        side: 'bottom',
        align: 'start',
        nextLabel: 'Continue',
      },
    },
  },
};

/**
 * Steps the tour can actually show on this page right now: centered cards,
 * steps of other pages (reached by navigating), steps that wait for an element
 * to appear, and steps whose anchor is on screen. Anything else is skipped so
 * the tour never explains a control that is not there.
 */
export function visibleSteps(
  section: TourSection,
  pageKey: string,
  exists: (target: string) => boolean
): Array<{ step: TourStep; index: number }> {
  return section.steps
    .map((step, index) => ({ step, index }))
    .filter(
      ({ step }) => step.page !== pageKey || !step.target || !!step.waitMs || exists(step.target)
    );
}

export const SECTION_ORDER: SectionId[] = SECTIONS.map((s) => s.id);

/** Sections listed in the Guide panel (the finish card is not replayable on its own). */
export const REPLAYABLE_SECTIONS = SECTIONS.filter((s) => s.id !== 'finish');

export function getSection(id: SectionId): TourSection {
  const section = SECTIONS.find((s) => s.id === id);
  if (!section) throw new Error(`Unknown tour section: ${id}`);
  return section;
}

export function findPage(section: TourSection, pathname: string): TourPage | undefined {
  return section.pages.find((p) => p.matches(pathname));
}

/** The auto-startable section whose page matches the current route, if any. */
export function findSectionByPath(
  pathname: string
): { section: TourSection; page: TourPage } | undefined {
  for (const section of SECTIONS) {
    if (!section.autoStart) continue;
    const page = findPage(section, pathname);
    if (page) return { section, page };
  }
  return undefined;
}

export function firstStepIndexOnPage(section: TourSection, pageKey: string): number {
  const idx = section.steps.findIndex((s) => s.page === pageKey);
  return idx === -1 ? 0 : idx;
}

/**
 * Where a run should resume on the given page: the stored step if it lives on
 * this page, otherwise the first step of this page.
 */
export function resolveStepIndex(section: TourSection, stepIndex: number, pageKey: string): number {
  const step = section.steps[stepIndex];
  if (step && step.page === pageKey) return stepIndex;
  return firstStepIndexOnPage(section, pageKey);
}

/** The section after `id` in page order (ending with the finish card), for runs started from the Guide panel. */
export function nextSectionInOrder(id: SectionId): TourSection | undefined {
  const i = SECTIONS.findIndex((s) => s.id === id);
  return i === -1 ? undefined : SECTIONS[i + 1];
}

/**
 * Where a run goes after `sectionId` ends by Next or Skip: the next pending
 * section for the first-time guide, the next section in order for a run
 * started from the Guide panel. Closing a run never chains.
 */
export function followingSection(
  sectionId: SectionId,
  kind: RunKind,
  statuses: Record<SectionId, SectionStatus>
): TourSection | undefined {
  return kind === 'guided' ? nextPendingSection(statuses) : nextSectionInOrder(sectionId);
}

/** Next section a guided run should move to, or undefined when the whole guide is finished. */
export function nextPendingSection(
  statuses: Record<SectionId, SectionStatus>
): TourSection | undefined {
  for (const section of SECTIONS) {
    if (section.id === 'finish') continue;
    if (statuses[section.id] === 'pending') return section;
  }
  return statuses.finish === 'pending' ? getSection('finish') : undefined;
}
